import type { FC } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import TablePricingBase, * as TablePricing from '@segment/matcha/recipes/TablePricing'
import { CheckIcon } from '@segment/matcha/recipes/TablePricing/Icons'

import useMatchMedia from '@segment/utils/useMatchMedia'

type Cell = boolean | string
const data: [string, Cell, Cell, Cell, Cell, Cell][] = [
  [
    `Will you provide incident support and facilitate provisioning of product code level fixes if encountered?`,
    true,
    true,
    true,
    true,
    true,
  ],
  [`Will you provide assistance with new feature releases?`, true, true, true, true, true],
  [`Will you provide assistance with documented implementation & product guidance?`, true, true, true, true, true],
  [`Will you help me with technical troubleshooting post implementation?`, true, true, true, true, true],
  [`Do you provide SLO on response and engagement time?`, `*Business Tier Only`, true, true, true, false],
  [`Will you join our project kick-off meeting?`, false, false, false, true, true],
  [
    `Will you help me map our requirements to best practice implementation directives?`,
    false,
    false,
    false,
    false,
    true,
  ],
  [`Will you own the delivery of my Segment project?`, false, false, false, false, true],
  [`Will you come on site?`, false, false, false, false, true],
  [`Will you provide system & performance tuning?`, false, false, false, false, true],
  [`Will you perform Health Checks on my Segment workspace?`, false, false, false, false, true],
  [`Will you troubleshoot my custom code for me?`, false, false, false, false, true],
  [`Will you own the technical design of my custom code for me?`, false, false, false, false, true],
  [`Will you build my customization for me from scratch?`, false, false, false, false, true],
  [`Will you provide training to help me understand how to use Segment?`, false, false, false, false, true],
  [`Will you write custom queries or build custom reports for my data?`, false, false, false, false, true],
]

const PricingTable: FC = () => {
  const theme = useTheme()
  const isDesktopResolution = useMatchMedia(theme.breakpoints.up(`xxl`, false), true)

  return (
    <SectionBase variant="noCopy" sticky={true}>
      <Section.Contents>
        <TablePricingBase columns={6}>
          <TablePricing.Header sticky={true} pinFirst={true} tokens={{ top: isDesktopResolution ? 76 : 0 }}>
            <TablePricing.HeaderCell title="Activity" headingVariant="h5" colSpan={3} />
            <TablePricing.HeaderCell title="Standard Support" headingVariant="h5" />
            <TablePricing.HeaderCell title="Advanced Support" headingVariant="h5" />
            <TablePricing.HeaderCell title="Premium Support" headingVariant="h5" />
            <TablePricing.HeaderCell title="Premium + Support" headingVariant="h5" />
            <TablePricing.HeaderCell
              title="Professional Services"
              headingVariant="h5"
              tokens={{ headingColor: theme.palette.twilio.boysenberry[60] }}
            />
          </TablePricing.Header>
          <TablePricing.Body zebra="odd">
            {data.map(([heading, ...row]) => (
              <TablePricing.Row
                key={heading}
                header={
                  <TablePricing.RowHeader tokens={{ variantDesktop: `pSmall`, variantMobile: `textRegularSmall` }}>
                    {heading}
                  </TablePricing.RowHeader>
                }
              >
                {row.map((cell, i) => (
                  <TablePricing.Cell key={`${heading}-${cell}-${i}`}>
                    {Boolean(cell) && (
                      <Stack>
                        <CheckIcon color={row.length - 1 === i ? theme.palette.twilio.boysenberry[60] : undefined} />
                        {typeof cell === `string` && <Description variant="textTiny">{cell}</Description>}
                      </Stack>
                    )}
                    {!cell && (
                      <Description variant="pMedium" tokens={{ color: theme.palette.twilio.gray[80] }}>
                        —
                      </Description>
                    )}
                  </TablePricing.Cell>
                ))}
              </TablePricing.Row>
            ))}
          </TablePricing.Body>
        </TablePricingBase>
      </Section.Contents>
    </SectionBase>
  )
}

export default PricingTable

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`

const Description = styled(Typography)`
  margin: 0;
`
